import { items } from '@lst/mk-entities/const';
import { default as MKEntity } from '@lst/mk-entities/wrappers/react/MKEntity';
import { DivTooltip, Interpolate, Message, Tooltip, TooltipBody, TooltipHeader } from '@lst/mk-react-uikit';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { splitAchievementReceivedCount } from '~/utils/achievements';

import styles from './Achievement.scss';

export interface Achievement_Props extends Achievements {
  isReceived: boolean;
  count?: number;
  lastTime?: number;
  shipId?: number;
}

const Achievement = (props: Achievement_Props) => {
  const {
    title,
    typeTitle,
    battleRestriction,
    receivingRestriction,
    description,
    icons,
    isReceived,
    count,
    lastTime,
    enabled,
    shipId,
    multiple,
    finalStatsTime,
  } = props;

  const { t } = useTranslation();
  const icon = isReceived ? icons.default : icons.inactive;
  const hasFinal = finalStatsTime !== -1;

  const renderTooltipAchievementNotEnabled = () => {
    if (enabled) {
      return null;
    }
    return (
      <React.Fragment>
        <div className={styles.tooltipBodyItemHr} />
        <Message style="inline" type="info-round-icon">
          {t('Больше недоступно для получения.')}
        </Message>
      </React.Fragment>
    );
  };

  const renderCountReceivedTooltip = () => {
    if (!multiple) {
      return null;
    }

    return <Interpolate str={t('Всего получено: %(count)s')} count={splitAchievementReceivedCount(count)} />;
  };

  const renderWaffentragerTimerTooltip = () => {
    const diffSec = Math.max(0, finalStatsTime - lastTime);
    const dur = moment.duration(diffSec, 'seconds');

    const days = Math.floor(dur.asDays());
    const hours = dur.hours();
    const minutes = dur.minutes();

    return (
      <>
        <div className={styles.tooltipBodyItemHr} />
        <div className={styles.timerTooltipWrapper}>
          <div>{t('До Резонанса оставалось:')}</div>
          <div className={styles.timerTooltip}>
            <Interpolate str={t('%(days)s день', { count: days })} days={days.toString()} />
            <Interpolate str={t('%(hours)s час', { count: hours })} hours={hours.toString()} />
            <Interpolate str={t('%(minutes)s минута', { count: minutes })} minutes={minutes.toString()} />
          </div>
        </div>
      </>
    );
  };

  const renderLastDateReceivedTooltip = () => {
    if (!multiple) {
      return null;
    }

    const achievementLastTime = lastTime ? moment(lastTime * 1000).format('DD.MM.YYYY') : '';

    return (
      <div className={styles.divTooltipTopMargin}>
        <Interpolate str={t('Последнее получение: %(date)s')} date={achievementLastTime} />
      </div>
    );
  };

  const renderShipReceivedTooltip = () => {
    if (!multiple || !shipId) {
      return null;
    }

    return (
      <div className={styles.tooltipVehicle}>
        {t('На корабле:')}
        &nbsp;
        <MKEntity
          type={items.VEHICLES}
          id={shipId}
          presentation={{
            withTooltip: false,
          }}
        />
      </div>
    );
  };

  const renderAchievementTooltipHeader = () => {
    const tooltipIconStyle = {
      background: `url("${icons.default}")`,
    };
    const titleStyle = isReceived ? styles.tooltipTitleTextIsReceived : styles.tooltipTitleTextNotIsReceived;
    const typeTitleStyle = isReceived
      ? styles.tooltipTitleAchievementTypeIsReceived
      : styles.tooltipTitleAchievementTypeNotReceived;

    return (
      <TooltipHeader isBold>
        <div style={{ display: 'flex' }}>
          <div className={styles.tooltipIcon} style={tooltipIconStyle} />
          <div className={styles.row}>
            <div className={titleStyle}>{title}</div>
            <div className={typeTitleStyle}>{typeTitle}</div>
          </div>
        </div>
      </TooltipHeader>
    );
  };

  const renderBattleRestriction = () => {
    return battleRestriction ? <div className={styles.divTooltipBattleRestriction}>{battleRestriction}</div> : null;
  };

  const renderReceivingRestriction = () => {
    return receivingRestriction ? (
      <div className={styles.divTooltipReceivingRestriction}>{receivingRestriction}</div>
    ) : null;
  };

  const renderTooltip = () => {
    if (isReceived) {
      return (
        <Tooltip>
          {renderAchievementTooltipHeader()}
          <TooltipBody>
            <Message style="inline" type="done">
              {t('Получено')}
            </Message>
            <div className={styles.tooltipBodyItemHr} />
            {renderCountReceivedTooltip()}
            {!hasFinal && renderLastDateReceivedTooltip()}
            {renderShipReceivedTooltip()}
            {!hasFinal && multiple && <div className={styles.tooltipBodyItemHr} />}
            <div className={styles.descStyle} dangerouslySetInnerHTML={{ __html: description }} />
            {renderBattleRestriction()}
            {renderReceivingRestriction()}
            {hasFinal && renderWaffentragerTimerTooltip()}
            {renderTooltipAchievementNotEnabled()}
          </TooltipBody>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip>
          {renderAchievementTooltipHeader()}
          <TooltipBody>
            <p className={styles.descStyle}>{description}</p>
          </TooltipBody>
        </Tooltip>
      );
    }
  };
  const DivTooltipStyle = isReceived ? styles.receivedDivTooltip : styles.notReceivedDivTooltip;

  return (
    <div className={styles.wrapper}>
      <DivTooltip className={DivTooltipStyle} tooltipBody={renderTooltip()}>
        <div
          className={styles.icon}
          style={{
            backgroundImage: `url("${icon}")`,
          }}
        >
          {isReceived && count > 1 && <div className={styles.count}>{`x${count}`}</div>}
        </div>
      </DivTooltip>
    </div>
  );
};

export default Achievement;
